import React, { useCallback, useEffect } from 'react';
import { Box, IconButton, Typography } from "@mui/material";
import { FileDownload as FileDownloadIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { MaterialReactTable } from 'material-react-table';
import Events from '../../../events';
import { EventsType } from '../../../events-types';
import { getByFilters, getObjectsByFieldOnDegradedCompound, getObjectsByRegEx, PRODUCT_DEGRADATION } from '../../../api/Products';
import { iBaseCardWithComponentProps, iBaseInnerCardProps } from "../../types";
import Button from "@mui/material/Button";
import { CardWithBaseComponent } from "../../common/BaseCard";
import { getColumns, getInitialColumnState } from "../../../utils/tables";
import { OnChangeFn, ColumnFiltersState } from "@tanstack/react-table";
import { exportToExcel, exportToExcelSelectedRows } from "../../../utils/export_xlsx";
import { ProductDegradation } from "./types";

const _ProductsDegradationTable: React.FC<iBaseInnerCardProps> = ({ data,
                                                                      setColumnFilters, columnFilters,
                                                                      isActive,
                                                                      searchListBy, searchListByFilters }) => {
    const UUID = '_ProductsDegradationTable';

    const columnNames = Object.keys(ProductDegradation);
    const columnsToView = ['TYPE', 'NAME', 'SETTINGS', 'DATE_CREATED'];

    const columns = getColumns(ProductDegradation, 0);
    const _initial_columns_state = getInitialColumnState(columnNames, columnsToView);

    // SEARCH FUNCTIONS
    const _search = useCallback((text: string) => {
        if (text.length > 0) {
            searchListBy?.(getObjectsByRegEx, PRODUCT_DEGRADATION,
                'TYPE,SOURCE,NAME,SETTINGS,DESCRIPTION', text);
        }
    }, [searchListBy]);

    const searchEvent = useCallback(async (event: { detail: string }) => {
        _search(event.detail);
    }, [_search]);

    const searchWizard = useCallback(async (event: { detail: any }) => {
        if (event.detail.component === PRODUCT_DEGRADATION) {
            searchListByFilters?.(getByFilters, PRODUCT_DEGRADATION, event.detail.filters, 'wizard result');
        }
    }, [searchListByFilters]);

    const searchByInchiKeyEvent = useCallback(async (event: { detail: string }) => {
        searchListBy?.(getObjectsByFieldOnDegradedCompound, PRODUCT_DEGRADATION, 'INCHIKEY', event.detail);
    }, [searchListBy]);

    // EVENTS
    useEffect(() => {
        if (isActive) {
            Events.on(EventsType.SEARCH, searchEvent, UUID);
            Events.on(EventsType.SEARCH_WIZARD, searchWizard, UUID);
            Events.on(EventsType.SEARCH_BY_INCHIKEY, searchByInchiKeyEvent, UUID);
        }
        return () => {
            Events.off(EventsType.SEARCH, UUID);
            Events.off(EventsType.SEARCH_WIZARD, UUID);
            Events.off(EventsType.SEARCH_BY_INCHIKEY, UUID);
        };
    }, [isActive, searchEvent, searchWizard, searchByInchiKeyEvent]);

    // RENDER FUNCTIONS
    const handleFilterFnChange: OnChangeFn<ColumnFiltersState> = (filters: any) => {
        setColumnFilters(filters);
    };

    return (
        <MaterialReactTable
            columns={columns}
            data={data ?? []}
            enableRowSelection
            enableColumnOrdering
            enableGlobalFilter={false}
            enableDensityToggle={true}
            initialState={{
                columnVisibility: _initial_columns_state,
                density: 'compact',
                columnOrder: [
                    'mrt-row-actions',
                    'mrt-row-select',
                    'mrt-row-expand',
                    'TYPE',
                    'NAME',
                    'SETTINGS',
                    'DATE_CREATED',
                ],
            }}
            renderDetailPanel={({ row }) => (
                <Box
                    sx={{
                        display: 'grid',
                        margin: 'auto',
                        gridTemplateColumns: '1fr 1fr',
                        width: '100%',
                    }}
                >
                    <Typography><b>Description:</b> {row.original.DESCRIPTION}</Typography>
                </Box>
            )}
            onColumnFiltersChange={handleFilterFnChange}
            state={{ columnFilters }}
            enableRowActions={true}
            renderRowActions={({ row }) => (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                    <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                            Events.trigger(EventsType.SEARCH_BY_PRODUCT_DEGRADATION, row.original);
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </Box>
            )}
            positionToolbarAlertBanner="bottom"
            renderTopToolbarCustomActions={({ table }) => (
                <Box
                    sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
                >
                    <Button
                        color="primary"
                        onClick={() => exportToExcel(data, "ProductsDegradation")}
                        startIcon={<FileDownloadIcon />}
                        variant="contained"
                    >
                        Export All Data
                    </Button>
                    <Button
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => exportToExcelSelectedRows(table.getSelectedRowModel().rows, "ProductsDegradation")}
                        startIcon={<FileDownloadIcon />}
                        variant="contained"
                    >
                        Export Selected Rows
                    </Button>
                </Box>
            )}
        />
    );
};

const ProductsDegradationTable: React.FC<Omit<iBaseCardWithComponentProps, 'cardComponent'>> = ({ header, width, eventGroupToTrigger }) => {
    return <CardWithBaseComponent cardComponent={_ProductsDegradationTable} header={header} width={width} eventGroupToTrigger={eventGroupToTrigger} />;
};

export default React.memo(ProductsDegradationTable);
